import { useState, useRef } from 'react';
import { Upload, X, File, CheckCircle2 } from 'lucide-react';
import { toast } from '@/stores/toastStore';
import Button from './Button';

interface FileUploadProps {
  label: string;
  accept: string;
  maxSize: number; // in MB
  currentFile?: string;
  onUpload: (file: File) => Promise<void>;
  onDelete?: () => Promise<void>;
  type?: 'document' | 'image';
  helperText?: string;
}

export default function FileUpload({
  label,
  accept,
  maxSize,
  currentFile,
  onUpload,
  onDelete,
  type = 'document',
  helperText,
}: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`File size must be less than ${maxSize}MB`);
      return;
    }

    // Validate file type
    const fileType = file.type;
    const acceptedTypes = accept.split(',').map((t) => t.trim());
    const isValidType = acceptedTypes.some((acceptedType) => {
      if (acceptedType.endsWith('/*')) {
        const category = acceptedType.split('/')[0];
        return fileType.startsWith(category + '/');
      }
      return fileType === acceptedType || file.name.endsWith(acceptedType.replace('*', ''));
    });

    if (!isValidType) {
      toast.error('Invalid file type');
      return;
    }

    // Show preview for images
    if (type === 'image' && fileType.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      await onUpload(file);

      clearInterval(progressInterval);
      setUploadProgress(100);
      toast.success(`${label} uploaded successfully!`);

      // Reset after showing success
      setTimeout(() => {
        setUploadProgress(0);
        setPreview(null);
      }, 2000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to upload file');
      setPreview(null);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (!confirm('Are you sure you want to delete this file?')) return;

    setIsDeleting(true);
    try {
      await onDelete();
      toast.success(`${label} deleted successfully!`);
      setPreview(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete file');
    } finally {
      setIsDeleting(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="glass-card p-6 border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{label}</h3>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
        disabled={isUploading || isDeleting}
      />

      <div className="space-y-4">
        {/* Current file or preview */}
        {(currentFile || preview) && (
          <div className="glass-card p-4 border border-gray-200 bg-gray-50">
            <div className="flex items-center gap-4">
              {type === 'image' && (preview || currentFile) ? (
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-200 border border-gray-300">
                  <img
                    src={preview || currentFile}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-blue-100">
                  <File className="w-8 h-8 text-blue-600" />
                </div>
              )}
              <div className="flex-1">
                <p className="text-gray-900 font-medium">
                  {preview ? 'Uploading...' : 'Current file'}
                </p>
                <p className="text-sm text-gray-600">
                  {type === 'image' ? 'Profile photo' : 'Document uploaded'}
                </p>
              </div>
              {!preview && onDelete && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  isLoading={isDeleting}
                  className="text-red-600 hover:bg-red-50 border-red-300 flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </Button>
              )}
            </div>

            {/* Upload progress */}
            {isUploading && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Success indicator */}
            {uploadProgress === 100 && (
              <div className="mt-3 flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                <span>Upload complete!</span>
              </div>
            )}
          </div>
        )}

        {/* Upload button and info */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            {helperText && (
              <p className="text-gray-600 text-sm mb-2">{helperText}</p>
            )}
            <p className="text-gray-500 text-xs">
              Accepted formats: {accept.split(',').join(', ')} (Max {maxSize}MB)
            </p>
          </div>
          <Button
            variant={currentFile ? 'outline' : 'primary'}
            onClick={triggerFileInput}
            disabled={isUploading || isDeleting}
            className="sm:w-auto"
          >
            <Upload className="w-5 h-5 sm:mr-2" />
            <span className="hidden sm:inline">{currentFile ? 'Replace' : 'Upload'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
